import template from 'header/utility-currency.j2';
import Cookies from '../services/cookies';
import { parse } from 'url';

import { updateLocationWithNewQueryStrings } from '../../../core/js/utils';
import { changeCurrency, oneYearInSeconds } from '../utils/currencyUtils';
import { getDefaultCurrencyForCountry } from '../sagas/currencySagas';

import {
    COOKIE_NAME_SITE_ID,
    COOKIE_NAME_SITE_ID_OLD,
    COOKIE_NAME_COUNTRY
} from '../constants';

import { generate } from '../../../core/js/utils/componentUtils';
import { getDefaultOptions } from '../../../core/js/utils/a15componentUtils';

import localStorageCache from '../../../core/js/utils/cacheUtils';
import { mDom } from '../../../core/js/utils/renderUtils';
import addDropdownSupport from '../../../core/js/uiSupport/dropDown';
import { clearUniqueVisitsCookie } from '../../../core/js/utils/cookieUtils';
import { sanitizeURL } from '../utils/urlUtils';
import { isBlankString } from '../utils/stringUtils';

import { error as logError } from '../utils/logger';
import { getFlatNavigationSync } from '@core/js/utils/navigationUtils';

export function init(store, overrides = {}) {
    const config = {
        templateLocation: '.dom-utility-currency',
        watch: [
            'core.header.availableSites',
            'core.deviceCharacteristics',
            'core.countryDisplayName',
            'core.currencyFormattingInfo.currencyPrefix'
        ],
        name: 'currencyComponent',
        afterRender: afterRender,
        firstRender: afterRender,
        attachEvents: attachEvents,
        template: template,
        renderType: mDom
    };
    return generate(config, store, overrides, getDefaultOptions(store));
}

function afterRender(store) {
    addDropdownSupport(this, this.templateLocation);
}

function getFlatNavLinksForCategorySlug(store, slug) {
    const flatNav = getFlatNavigationSync(store);

    return _.get(flatNav, `${slug}.links`, []);
}

export function getTranslatedURLFromFlatNavigation(url, flatNavSlug, store, toLanguage) {
    const state = store.getState();
    const countriesList = _.get(state, 'core.constants.utilityNavOptions.countriesList', []);

    const toLanguageCountry = _.find(countriesList, (countryInfo) => {
        return countryInfo.contextPath === toLanguage;
    });

    const urlParts = parse(url);

    // remove trailing slash from url pathname (if any) to compare with flat nav slug
    const normalizedURLPath = _.trimEnd(urlParts.pathname, '/');

    if (flatNavSlug && _.endsWith(normalizedURLPath, flatNavSlug)) {
        const categoryLinks = getFlatNavLinksForCategorySlug(store, flatNavSlug);

        for (let index = 0; index <= categoryLinks.length; index++) {
            let newToLanguage = toLanguage;

            if (toLanguageCountry) {
                newToLanguage = _.get(toLanguageCountry, 'locale');
            }

            const locale = _.get(categoryLinks[index], 'locale');
            const newSlug = _.get(categoryLinks[index], 'slug');

            if (newSlug && locale === newToLanguage) {
                return url.replace(flatNavSlug, newSlug);
            }
        }

        return url;
    }

    return url;
}

export function translateCategoryURL(url, store, toLanguage) {
    const state = store.getState();
    const oldCategorySlug = _.get(state, 'category.categorySlug');
    return getTranslatedURLFromFlatNavigation(url, oldCategorySlug, store, toLanguage);
}

export function translateHelpURL(url, store, toLanguage) {
    const state = store.getState();
    const helpSlug = _.get(state, 'help.slug');
    return getTranslatedURLFromFlatNavigation(url, helpSlug, store, toLanguage);
}

export function translateProductURL(url, store, toLanguage) {
    const state = store.getState();
    const oldProductSlug = _.get(state, 'product.productSlug');
    let result = url;
    const urlParts = parse(url);

    if (oldProductSlug && _.endsWith(urlParts.pathname, oldProductSlug)) {
        const productLinks = _.get(state, 'product.links', []);

        // translate product slug
        for (let index = 0; index <= productLinks.length; index++) {
            const locale = _.get(productLinks[index], 'locale');
            const newSlug = _.get(productLinks[index], 'productSlug');

            if (newSlug && locale === toLanguage) {
                result = url.replace(oldProductSlug, newSlug);

                // try to translate possible category query parameter
                const oldCategorySlug = _.get(state, 'product.browseCategory');

                if (oldCategorySlug && result.indexOf(`category=${oldCategorySlug}`) !== -1) {
                    const categoryLinks = getFlatNavLinksForCategorySlug(store, oldCategorySlug);

                    for (let index2 = 0; index2 <= categoryLinks.length; index2++) {
                        const locale2 = _.get(categoryLinks[index2], 'locale');
                        const newSlug2 = _.get(categoryLinks[index2], 'slug');

                        if (newSlug2 && locale2 === toLanguage) {
                            result = result.replace(oldCategorySlug, newSlug2);
                        }
                    }
                }

                return result;
            }
        }

        return url;
    }

    return url;
}

function removeDoubleSlashesFromURLPath(path) {
    let result = _.clone(path);

    while (_.includes(result, '//')) {
        result = result.replace(/\/\//g, '/');
    }

    return result;
}

export function switchContextPath(siteId, urlPathname, toLanguage, contextPath) {
    const contextPathRegex = /^\/([a-zA-Z]{2}-[a-zA-Z]{2})\/?/;
    const match = urlPathname.match(contextPathRegex);
    const isFPSite = _.startsWith(siteId, 'fp');

    if (!isFPSite) {
        if (match) {
            const newContextPath = (toLanguage === 'en-US' ? '' : toLanguage.toLowerCase());
            return removeDoubleSlashesFromURLPath(urlPathname.replace(match[1], newContextPath));
        }

        // assume urlPathname was to en-US site
        if (toLanguage === 'en-US') {
            const result = (urlPathname.trim().length === 0 ? '/' : urlPathname);
            return removeDoubleSlashesFromURLPath(result);
        }

        const contextPathPosition = urlPathname.indexOf(`/${toLanguage.toLowerCase()}`);

        if (contextPathPosition === 0) {
            return removeDoubleSlashesFromURLPath(urlPathname);
        }

        return removeDoubleSlashesFromURLPath(`/${toLanguage.toLowerCase()}${urlPathname}`);
    }

    const contextPathInURL = _.startsWith(urlPathname, contextPath);
    const contextPathInURLWithTrailingSlash = _.startsWith(`${urlPathname}/`, contextPath);
    let normalizedURLPath = _.clone(urlPathname);

    if (!contextPathInURL && contextPathInURLWithTrailingSlash) {
        normalizedURLPath = `${urlPathname}/`;
    }

    const newPathName = normalizedURLPath.replace(contextPath, toLanguage.toLowerCase());
    return removeDoubleSlashesFromURLPath(`/${newPathName}`);
}

export function translateURL(store, toLanguage) {
    const currentURL = window.location.pathname;
    const state = store.getState();
    const contextPath = _.get(state, 'core.contextPath');
    const siteId = _.get(state, 'core.siteId');

    const urlWithNewContextPath = switchContextPath(siteId, currentURL, toLanguage, contextPath);

    if (!_.isEmpty(state.category)) {
        return translateCategoryURL(urlWithNewContextPath, store, toLanguage);
    } else if (!_.isEmpty(state.product)) {
        return translateProductURL(urlWithNewContextPath, store, toLanguage);
    } else if (!_.isEmpty(state.help)) {
        return translateHelpURL(urlWithNewContextPath, store, toLanguage);
    }

    return urlWithNewContextPath;
}

export function attachEvents(store) {
    $(document).on(this.scopedEvent('touchend click'), this.scopedSelector('.js-site-language-change'),
        function siteLanguageChange(event) {
            event.preventDefault();

            const newLocale = $(this).attr('data-js-locale');
            let countryCode = $(this).attr('data-js-iso');

            const state = store.getState();
            const translatedURL = translateURL(store, newLocale);
            let changedCurrency;

            localStorageCache.clear();
            clearUniqueVisitsCookie();

            // for non-FP sites
            if (!countryCode) {
                countryCode = _.last(newLocale.split('-'));
            }

            store.runSaga(getDefaultCurrencyForCountry, countryCode).done.then((defaultCurrency) => {
                if (defaultCurrency && _.size(defaultCurrency) > 0) {
                    changedCurrency = defaultCurrency;
                    changeCurrency(defaultCurrency, oneYearInSeconds, false);
                }
            }).then(() => {
                const newContextPath = _.toLower(sanitizeURL(`/${_.trimEnd(newLocale, '/')}`));
                const newSiteId = _.get(state, `core.constants.siteIdToContextPathMappings.${newContextPath}.siteId`);

                if (newSiteId) {
                    Cookies.setCookie(COOKIE_NAME_SITE_ID, newSiteId);
                    Cookies.setCookie(COOKIE_NAME_SITE_ID_OLD, newSiteId);
                }
            })
            .catch((err) => {
                logError(`Error looking up currency info for ${countryCode}`, err);
            })
            .then(() => {  // finally case
                if (translatedURL) {
                    const addedQueryParams = {
                        ref: 'languageSelect'
                    };

                    if (countryCode) {
                        addedQueryParams.countryCode = countryCode;
                    }

                    if (changedCurrency) {
                        addedQueryParams.currency = changedCurrency;
                    }

                    const newQueryParams = updateLocationWithNewQueryStrings(addedQueryParams);

                    if (!isBlankString(countryCode)) {
                        Cookies.setCookie(COOKIE_NAME_COUNTRY, _.toUpper(countryCode));
                    }

                    window.location = `${translatedURL}?${newQueryParams}`;
                } else {
                    // IE support
                    window.location = window.location;
                }
            });
        });

    $(document).on(this.scopedEvent('touchend click'), this.scopedSelector('.js-site-currency-change'), (event) => {
        event.preventDefault();
        const newCurrency = $(event.target).attr('data-js-currency');

        changeCurrency(newCurrency);
    });

    $(document).on(this.scopedEvent('touchend click'), '.js-currency-toggle', (event) => {
        event.preventDefault();
    });
}
